import { getFileId, createNewFile, updateFile, executeWithAuthentication, isTokenValid, refreshAccessToken } from '@utility/google_utils';
const DB_NAME = "workoutDB";
const DB_VERSION = 25;
// TODO: unify the db name/version and utility functions across pages

export const openDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      console.log(`Upgrading database to version ${DB_VERSION}`);

      // List of all stores with their configurations
      const storeConfigs = [
        // Current workout stores
        {
          name: "current_workout_log",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [{ name: "session_id", keyPath: "session_id", options: { unique: false } }]
        },
        {
          name: "current_sets",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [{ name: "workout_log_id", keyPath: "workout_log_id", options: { unique: false } }]
        },
        {
          name: "current_workout_session",
          options: { keyPath: "id", autoIncrement: true }
        },

        // Workout history stores
        {
          name: "workout_sessions",
          options: { keyPath: "id", autoIncrement: true }
        },
        {
          name: "workout_log",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [{ name: "session_id", keyPath: "session_id", options: { unique: false } }]
        },
        {
          name: "sets",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [{ name: "workout_log_id", keyPath: "workout_log_id", options: { unique: false } }]
        },

        // Template stores
        {
          name: "workout_templates",
          options: { keyPath: "id", autoIncrement: true }
        },
        {
          name: "workout_template_exercises",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [
            { name: "template_id", keyPath: "template_id", options: { unique: false } },
            { name: "exercise_id", keyPath: "exercise_id", options: { unique: false } }
          ]
        },

        // Exercise and muscle data
        {
          name: "exercises",
          options: { keyPath: "id", autoIncrement: true }
        },
        {
          name: "muscles",
          options: { keyPath: "id", autoIncrement: true }
        },
        {
          name: "muscle_groups",
          options: { keyPath: "id", autoIncrement: true }
        },
        {
          name: "muscle_to_group",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [
            { name: "muscle_id", keyPath: "muscle_id", options: { unique: false } },
            { name: "group_id", keyPath: "group_id", options: { unique: false } }
          ]
        },
        {
          name: "exercise_primary_muscles",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [
            { name: "exercise_id", keyPath: "exercise_id", options: { unique: false } },
            { name: "muscle_id", keyPath: "muscle_id", options: { unique: false } }
          ]
        },
        {
          name: "exercise_secondary_muscles",
          options: { keyPath: "id", autoIncrement: true },
          indexes: [
            { name: "exercise_id", keyPath: "exercise_id", options: { unique: false } },
            { name: "muscle_id", keyPath: "muscle_id", options: { unique: false } }
          ]
        },

        // User data stores
        {
          name: "userdata",
          options: { keyPath: "id", autoIncrement: true }
        },
        {
          name: "user_weight_log",
          options: { keyPath: "id", autoIncrement: true }
        },

        // Performance tracking - CORRECTED implementation
        {
          name: "last_exercise_performance",
          options: { keyPath: "exercise_id", autoIncrement: false },
          indexes: [{ name: "by_date", keyPath: "last_session_date", options: { unique: false } }]
        }
      ];

      // Create or upgrade all stores
      storeConfigs.forEach(config => {
        if (db.objectStoreNames.contains(config.name)) {
          console.log(`Deleting old ${config.name} store`);
          db.deleteObjectStore(config.name);
        }

        console.log(`Creating ${config.name} store`);
        const store = db.createObjectStore(config.name, config.options);

        if (config.indexes) {
          config.indexes.forEach(index => {
            console.log(`Creating index ${index.name} for ${config.name}`);
            store.createIndex(index.name, index.keyPath, index.options);
          });
        }
      });
    };

    request.onsuccess = (event) => {
      const db = event.target.result;
      console.log("Database opened successfully");
      
      // Verify the last_exercise_performance store
      if (!db.objectStoreNames.contains("last_exercise_performance")) {
        console.error("Performance store creation failed!");
      }
      
      resolve(db);
    };

    request.onerror = (event) => {
      console.error("Database error:", event.target.error);
      reject(event.target.error);
    };

    request.onblocked = () => {
      console.warn("Database access blocked - please close other tabs");
      reject(new Error("Database access blocked"));
    };
  });
};

// Function to store data into IndexedDB
export function storeJSONData(json_data) {
  const request = indexedDB.open(DB_NAME, DB_VERSION);

  // Ensure the DB is upgraded to the correct schema
  request.onupgradeneeded = (event) => {
  const db = event.target.result;

  json_data.tables.forEach(table => {
    if (!db.objectStoreNames.contains(table.name)) {
      // Special case for last_exercise_performance
      if (table.name === "last_exercise_performance") {
        const store = db.createObjectStore(table.name, { 
          keyPath: "exercise_id", 
          autoIncrement: false 
        });
        store.createIndex("by_date", "last_session_date", { unique: false });
      } else {
        // Default case for other tables
        let store = db.createObjectStore(table.name, { 
          keyPath: "id", 
          autoIncrement: true 
        });
        table.columns.forEach(column => {
          if (column !== "id") {
            store.createIndex(column, column, { unique: false });
          }
        });
      }
    }
  });
};

  // Once the database is successfully opened, insert data
  request.onsuccess = (event) => {
    const db = event.target.result;
    insertData(db, json_data);
  };

  request.onerror = (event) => {
    console.error("Database error:", event.target.error);
  };
}

function insertData(db, json_data) {
  json_data.tables.forEach(table => {
      const transaction = db.transaction(table.name, "readwrite");
      const store = transaction.objectStore(table.name);

      table.rows.forEach(row => {
          let record = {};
          table.columns.forEach((column, index) => {
              record[column] = row[index];
          });

          store.put(record);
      });
  });
}
  // Function to retrieve data from IndexedDB for a given table
export const getTableData = async (tableName) => {
  const db = await openDB();
  const transaction = db.transaction(tableName, "readonly");
  const store = transaction.objectStore(tableName);
  
  return new Promise((resolve, reject) => {
    const request = store.getAll();  // Fetch all data from the store
    request.onsuccess = () => resolve(request.result);
    request.onerror = (e) => reject(e.target.error);
  });
};

export async function exportIndexedDB({ uploadToGoogleDrive = false, localExport = false }) {
  try {
    // Export IndexedDB data to JSON
    const db = await openDB();
    const tables = Array.from(db.objectStoreNames);
    const result = {
      tables: []
    };
    // check if table is empty before export
    let hasData = false;

    // Process each table sequentially
    for (const tableName of tables) {
      const tableData = await getTableData(tableName);
      
      if (tableData.length > 0) {
        if (tableData.length > 1) {
          hasData = true;
        }
        
        // Get all unique column names from the records
        const columns = Object.keys(tableData[0]);
        
        // Convert objects to arrays in the same order as columns
        const rows = tableData.map(item => {
          return columns.map(column => item[column]);
        });
        
        result.tables.push({
          name: tableName,
          columns: columns,
          rows: rows
        });
      } else {
        // Handle empty tables
        result.tables.push({
          name: tableName,
          columns: [],
          rows: []
        });
      }
    }

    if (!hasData) {
      return "Database is empty. No data to export.";
    }
    
    // Convert to JSON string
    const jsonString = JSON.stringify(result, null, 2)
      .replace(/\[\n\s+/g, '[')
      .replace(/,\n\s+/g, ', ')
      .replace(/\n\s+\]/g, ']');
    
    // Create blob from JSON string
    const fileBlob = new Blob([jsonString], { type: 'application/json' });

    // Handle local export
    if (localExport) {
      await saveFileLocally(fileBlob, 'freeweight_database.json');
      return "Database exported locally.";
    }

    // Handle Google Drive upload if requested
    if (uploadToGoogleDrive) {
      // Get the access token from localStorage
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        throw new Error('AUTH_REQUIRED');
      }

      // Check token validity before proceeding
      if (!(await isTokenValid(accessToken))) {
        try {
          const newToken = await refreshAccessToken();
          if (!newToken) throw new Error('AUTH_REQUIRED');
        } catch (refreshError) {
          throw new Error('AUTH_REQUIRED');
        }
      }

      // Use executeWithAuthentication wrapper for Google Drive operations
      return await executeWithAuthentication(async (token) => {
        // Find the existing file ID
        const fileName = "freeweight_database.json";
        const fileId = await getFileId(token, fileName);
        
        if (fileId) {
          // Update existing file
          await updateFile(token, fileId, fileBlob);
          return `Successfully updated "${fileName}" in Google Drive.`;
        } else {
          // Create new file if it doesn't exist
          await createNewFile(token, fileName, fileBlob);
          return `"${fileName}" created in Google Drive.`;
        }
      });
    }

    return "No export destination specified.";
  } catch (error) {
    console.error("Export failed:", error);
    
    // Rethrow AUTH_REQUIRED error for proper handling in the caller
    if (error.message === 'AUTH_REQUIRED') {
      throw error;
    }
    
    throw new Error(`Export failed: ${error.message}`);
  }
}

// Helper function to save file locally using File System Access API
async function saveFileLocally(blob, defaultFileName) {
  try {
    // Check for File System Access API support
    if ('showSaveFilePicker' in window) {
      const options = {
        types: [
          {
            description: 'JSON Files',
            accept: {
              'application/json': ['.json'],
            },
          },
        ],
        suggestedName: defaultFileName,
      };
      
      const fileHandle = await window.showSaveFilePicker(options);
      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
    } else {
      // Fallback for browsers without File System Access API
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = defaultFileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  } catch (error) {
    console.error('Error saving file locally:', error);
    throw error;
  }
}

export function deleteIndexedDbTable() {
    return new Promise(async (resolve, reject) => {
        // Nuclear option 1: Force-close all connections to this DB
        try {
            // This forces all connections to close
            if (window.indexedDB.databases) {
                const databases = await window.indexedDB.databases();
                for (const dbInfo of databases) {
                    if (dbInfo.name === DB_NAME) {
                        // This will force-close any open connections
                        const req = indexedDB.deleteDatabase(dbInfo.name);
                        req.onblocked = () => {
                            // Nuclear option 2: If still blocked, retry with timeout
                            console.warn("DB still blocked - forcing through");
                            setTimeout(() => {
                                indexedDB.deleteDatabase(dbInfo.name);
                            }, 100);
                        };
                        req.onsuccess = resolve;
                        req.onerror = reject;
                        return;
                    }
                }
            }
            
            // Fallback if databases() isn't supported
            const req = indexedDB.deleteDatabase(DB_NAME);
            req.onblocked = () => {
                console.warn("DB blocked - forcing deletion anyway");
                // Just proceed with deletion despite blocked warning
                resolve();
            };
            req.onsuccess = resolve;
            req.onerror = reject;
            
        } catch (error) {
            console.error("Nuclear deletion failed:", error);
            reject(error);
        }
    });
}

export const deleteIndexedDBData = (storeName, id, indexName = 'id') => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open('workoutDB');
        
        request.onsuccess = (event) => {
            const db = event.target.result;
            
            // Check if the object store exists
            if (!db.objectStoreNames.contains(storeName)) {
                console.log(`Object store ${storeName} does not exist`);
                db.close();
                resolve();
                return;
            }
            
            const transaction = db.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            
            // Check if the index exists
            if (!store.indexNames.contains(indexName)) {
                console.log(`Index ${indexName} does not exist in store ${storeName}`);
                db.close();
                resolve();
                return;
            }
            
            const index = store.index(indexName);
            const deleteRequest = index.openCursor(IDBKeyRange.only(id));
            
            deleteRequest.onsuccess = (e) => {
                const cursor = e.target.result;
                if (cursor) {
                    cursor.delete();
                    cursor.continue();
                } else {
                    resolve();
                }
            };
            
            deleteRequest.onerror = (e) => {
                db.close();
                reject(e.target.error);
            };
        };
        
        request.onerror = (event) => {
            // Handle case where database doesn't exist
            if (event.target.error.name === 'NotFoundError') {
                console.log('Database does not exist - nothing to delete');
                resolve();
            } else {
                reject(event.target.error);
            }
        };
        
        request.onupgradeneeded = (event) => {
            // This shouldn't happen since we're just opening, but handle it
            console.log('Database upgrade needed during delete operation');
            event.target.transaction.abort();
        };
    });
};

export const addIndexedDBData = (storeName, data) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('workoutDB');
    
    request.onsuccess = (event) => {
      const db = event.target.result;
      const transaction = db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const addRequest = store.add(data);
      
      addRequest.onsuccess = () => resolve();
      addRequest.onerror = (e) => reject(e.target.error);
    };
    
    request.onerror = (event) => {
      reject(event.target.error);
    };
  });
};

/**
 * Checks if IndexedDB contains any data in the specified store
 * @param {string} storeName - The store to check (default: 'exercises')
 * @returns {Promise<boolean>} True if data exists
 */
export const checkIndexedDBForData = async (storeName = 'exercises') => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onsuccess = (event) => {
      const db = event.target.result;
      
      // Verify the store exists
      if (!db.objectStoreNames.contains(storeName)) {
        db.close();
        resolve(false);
        return;
      }

      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const countRequest = store.count();

      countRequest.onsuccess = () => {
        db.close();
        resolve(countRequest.result > 0);
      };

      countRequest.onerror = () => {
        db.close();
        resolve(false);
      };
    };

    request.onerror = () => {
      console.error('Error opening IndexedDB');
      resolve(false);
    };
  });
};

/**
 * Resets IndexedDB to default state with fallback data
 * @param {object} fallbackData - Data to use if default JSON fails
 * @returns {Promise<void>}
 */
export const resetIndexedDB = async (fallbackData = null) => {
  try {
    // 1. Clear existing database
    await deleteIndexedDbTable();
    
    // 2. Open fresh database which will trigger schema creation
    const db = await openDB();
    db.close();
    
    // 3. Load default data
    if (fallbackData) {
      await storeJSONData(fallbackData);
    } else {
      try {
        const response = await fetch('/freeweight_database.json');
        if (!response.ok) throw new Error('Failed to load default data');
        await storeJSONData(await response.json());
      } catch (error) {
        console.error('Failed to load default JSON, using fallback:', error);
        await storeJSONData({
          tables: [
            {
              name: "exercises",
              columns: ["id", "name", "description"],
              rows: []
            },
            // Add other essential tables with empty data
          ]
        });
      }
    }
  } catch (error) {
    console.error('Failed to reset IndexedDB:', error);
    throw error;
  }
};